import { useEffect, useRef, useState } from "react";
import { File, FilePdf, FileText, FilmStrip, Image as ImageIcon, LinkSimple, MusicNotes, Table, UploadSimple } from "@phosphor-icons/react";

import Panel from "@/ui/Panel";
import Button from "@/ui/Button";
import { ButtonTypes } from "@/ui/types";
import { getSessionToken } from "@/api/auth";
import { finishUpload, listMedia, sendToStorage, startUpload } from "@/api/media";
import { MediaFile, MediaType, addedLabel, filterFiles, uploadFiles } from "../library";

// Launch Media's dashboard widget (SM-7): a campaign's files on its own dashboard (campaign, site or
// page view). Supplied through the WIDGET hook (Apps/Media/index.tsx); always shown to anyone with
// Launch Media. Files dropped here go to the library already filed under this campaign.

const ICON: Record<MediaType, typeof File> = { image: ImageIcon, video: FilmStrip, audio: MusicNotes, pdf: FilePdf, doc: FileText, sheet: Table, other: File };

export const MediaFiles = ({ campaignData = {}, label = "Launch Media", max = 6 }: { campaignData?: any; label?: string; max?: number }) => {
  const campaignId: string | undefined = campaignData?.id;
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [over, setOver] = useState(false);
  const [note, setNote] = useState("");
  const picker = useRef<HTMLInputElement>(null);

  const load = async () => {
    try { const out = await listMedia(getSessionToken() || ""); setFiles(filterFiles(out.files || [], { type: null, tag: null, q: "", campaign: campaignId || null }).files); }
    catch (e) { setNote("Couldn't load this campaign's files."); }
    setLoading(false);
  };
  useEffect(() => { if (campaignId) load(); }, [campaignId]);

  const upload = async (list: FileList | null) => {
    const chosen = Array.from(list || []);
    if (!chosen.length || !campaignId) return;
    const token = getSessionToken() || "";
    setNote(`Uploading ${chosen.length} file${chosen.length === 1 ? "" : "s"}…`);
    const out = await uploadFiles(chosen, { start: (f) => startUpload(token, f), send: sendToStorage, finish: (key, name) => finishUpload(token, key, name, campaignId) }, () => {});
    setNote(out.failed.length ? `${out.failed[0].name}: ${out.failed[0].error}` : `Added ${out.added.length} file${out.added.length === 1 ? "" : "s"} to this campaign`);
    if (out.added.length) load();
  };
  const library = `/media${campaignId ? `?campaign=${encodeURIComponent(campaignId)}` : ""}`;

  return (
    <div
      className={`rounded-xl ${over ? "ring-2 ring-teal-500" : ""}`}
      onDragOver={(e) => { if (Array.from(e.dataTransfer?.types || []).includes("Files")) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return; e.preventDefault(); setOver(false); upload(e.dataTransfer.files); }}
    >
      <Panel
        title={
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-sm font-semibold">{label}</h2>
            {campaignData?.name && <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-teal-50 text-teal-800 dark:bg-teal-400/10 dark:text-teal-200"><LinkSimple size={10} />{campaignData.name}</span>}
            <a href={library} className="ml-auto text-xs font-semibold text-blue-600 dark:text-blue-300">Open in library</a>
          </div>
        }
        withItems
        items={
          loading ? [{ title: <p className="text-sm text-black/40 dark:text-white/40">Loading…</p>, description: "" }]
          : files.length ? files.slice(0, max).map((f) => {
              const Icon = ICON[f.type] || File;
              return {
                icon: <div className="w-7 h-7 mt-0.5 flex-none rounded-lg bg-black/5 dark:bg-white/10 flex items-center justify-center"><Icon size={15} /></div>,
                title: <p className="text-sm font-normal truncate">{f.name}</p>,
                description: addedLabel(f.addedAt),
                href: `${library}${library.includes("?") ? "&" : "?"}file=${encodeURIComponent(f.id)}`,
              };
            })
          : [{ title: <p className="text-sm text-black/40 dark:text-white/40">No files in this campaign yet.</p>, description: "" }]
        }
        footer={{
          caption: note || (over ? "Drop to add them to this campaign" : "Drop files here to add them to this campaign"),
          actionComponent: (
            <>
              <input ref={picker} type="file" multiple hidden onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
              <Button label="Choose files" type={ButtonTypes.SOFT} onClick={() => picker.current?.click()} defaultIcon={<UploadSimple size={14} />} />
            </>
          ),
        }}
      />
    </div>
  );
};
